"use client";

import { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, getApiErrorCode } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { PublicPlan } from "@/lib/types/plan";

/**
 * Picks a plan without leaving the dashboard.
 *
 * "Change plan" used to push the user to `/?upgrade=1#pricing`, the marketing
 * pricing section. That section sells: it promises a 14-day trial and no credit
 * card to someone who is already paying, and it carried a second copy of the
 * plan-state logic, which is where two separate billing bugs lived. This shows
 * the same `/api/plans` data shaped for the job an existing customer has.
 */
export function ChangePlanDialog({
  open,
  onOpenChange,
  currentPlanCode,
  hasSubscription,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Lowercased plan code the account is on now. */
  currentPlanCode: string;
  /** False during the trial: there is no Stripe subscription to modify yet. */
  hasSubscription: boolean;
  /** Called after a change that applied immediately, so the page can refresh. */
  onChanged: () => void;
}) {
  const t = useTranslations("dashboard.billing.change");
  const tErrors = useTranslations("api-errors");
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.plans
      .list()
      .then((r) => setPlans(r.data))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, [open]);

  const savingPercent = useMemo(() => {
    const savings = plans
      .filter((p) => p.monthlyAmountUsd > 0 && p.annualAmountUsd > 0)
      .map((p) => 1 - p.annualAmountUsd / (p.monthlyAmountUsd * 12));
    return savings.length ? Math.floor(Math.min(...savings) * 100) : 0;
  }, [plans]);

  const currentSortOrder =
    plans.find((p) => p.planCode === currentPlanCode)?.sortOrder ?? 0;

  const choose = async (plan: PublicPlan) => {
    setBusy(plan.planCode);
    try {
      const interval = annual ? "annual" : "monthly";

      // No subscription means no period to prorate against and nothing for
      // Stripe to modify, so this is a Checkout, not a plan change. Deciding
      // from the plan name instead sent every trial user into change-plan,
      // which can only answer 400.
      if (!hasSubscription) {
        const r = await api.billing.checkout({ planCode: plan.planCode, interval });
        // eslint-disable-next-line react-hooks/immutability
        window.location.href = r.url;
        return;
      }

      const r = await api.billing.changePlan({ planCode: plan.planCode, interval });
      if (r.immediate) {
        toast.success(t("switched", { plan: plan.displayName }));
      } else {
        const when = r.scheduledFor
          ? new Date(r.scheduledFor).toLocaleDateString()
          : t("nextRenewal");
        toast.success(t("scheduled", { plan: plan.displayName, date: when }));
      }
      onOpenChange(false);
      onChanged();
    } catch (e) {
      const failure = getApiErrorCode(e);
      toast.error(
        failure && tErrors.has(failure.errorCode)
          ? tErrors(failure.errorCode, failure.params as never)
          : t("failed"),
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("title")}</DialogTitle>
          <DialogDescription>
            {hasSubscription ? t("subtitleSubscribed") : t("subtitleTrial")}
          </DialogDescription>
        </DialogHeader>

        {!loading && plans.length > 0 && (
          <div className="flex items-center justify-center gap-3">
            <Label
              htmlFor="change-plan-interval"
              className={cn("cursor-pointer text-sm", !annual && "text-foreground")}
            >
              {t("monthly")}
            </Label>
            <Switch
              id="change-plan-interval"
              checked={annual}
              onCheckedChange={setAnnual}
            />
            <Label
              htmlFor="change-plan-interval"
              className={cn("cursor-pointer text-sm", annual && "text-foreground")}
            >
              {t("annual")} <span className="text-primary">{t("save", { percent: savingPercent })}</span>
            </Label>
          </div>
        )}

        {loading && (
          <p className="py-8 text-center text-sm text-muted-foreground">{t("loading")}</p>
        )}

        {!loading && plans.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">{t("empty")}</p>
        )}

        <div className="flex flex-col gap-2.5">
          {plans.map((plan) => {
            const isCurrent = hasSubscription && plan.planCode === currentPlanCode;
            // annualAmountUsd is the yearly total; both figures shown are per month.
            const perMonth = annual ? plan.annualAmountUsd / 12 : plan.monthlyAmountUsd;
            const isDowngrade = hasSubscription && plan.sortOrder < currentSortOrder;

            return (
              <div
                key={plan.planCode}
                data-testid="change-plan-row"
                data-current={isCurrent || undefined}
                className={cn(
                  "flex flex-wrap items-center gap-4 rounded-xl border p-4",
                  isCurrent ? "border-primary/40 bg-primary/10" : "border-border bg-card",
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{plan.displayName}</span>
                    {isCurrent && (
                      <span className="rounded-full bg-primary/20 px-2 py-0.5 text-[11px] font-semibold text-primary">
                        {t("current")}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {plan.limits.maxSignalsPerDay === -1
                      ? t("unlimitedPerDay")
                      : t("perDay", { count: plan.limits.maxSignalsPerDay.toLocaleString() })}
                    {" · "}
                    {t("sourcesAndDestinations", {
                      sources: plan.limits.maxSources,
                      destinations: plan.limits.maxConnections,
                    })}
                  </p>
                </div>

                <div className="text-end">
                  <div className="font-heading text-lg font-bold">
                    ${perMonth.toFixed(2)}
                    <span className="text-xs font-normal text-muted-foreground">{t("perMonth")}</span>
                  </div>
                  {annual && (
                    <p className="text-[11px] text-muted-foreground">
                      {t("billedYearly", { price: `$${plan.annualAmountUsd.toFixed(2)}` })}
                    </p>
                  )}
                </div>

                {isCurrent ? (
                  <Check aria-hidden="true" className="size-5 shrink-0 text-primary" />
                ) : (
                  <Button
                    size="sm"
                    variant={isDowngrade ? "glass" : "gradient"}
                    disabled={busy !== null}
                    onClick={() => choose(plan)}
                  >
                    {busy === plan.planCode ? "..." : t("select")}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* What actually happens on confirm. Previously the user pressed a
            button with no idea whether money moved today or in three weeks. */}
        {!loading && plans.length > 0 && (
          <p role="note" className="text-xs text-muted-foreground">
            {!hasSubscription
              ? t("noteTrial")
              : annual
                ? t("noteAnnual")
                : t("noteSubscribed")}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
